/**
 * Trade Application Form — auto customer account creation
 * ---------------------------------------------------------
 * IMPORTANT: The Storefront API's CustomerUpdateInput has no
 * "metafields" field — customer metafields can only be written via
 * the Admin API, never from unauthenticated client-side code. That
 * rules out writing them directly from this script. Instead:
 *
 * On submit, this script:
 *  1. Creates a real Shopify customer account via the Storefront API
 *     (customerCreate) using a securely generated random password.
 *  2. Sends the trade application fields to a Shopify Flow "Receive
 *     webhook" trigger (server-side, Admin API access), which finds
 *     the customer by email and writes the metafields there, and
 *     also emails the store owner about the new application.
 *  3. Triggers Shopify's native "customer recover" flow so the
 *     customer gets a real password-reset email and can set their
 *     own password.
 *
 * Requires:
 *  - A Storefront API access token with unauthenticated customer
 *    read/write scopes, set in Theme Settings > Trade / wholesale
 *    pricing > Storefront API access token.
 *  - A Shopify Flow webhook URL (see Theme Settings > Trade /
 *    wholesale pricing > Flow webhook URL) built with a "Receive
 *    webhook" trigger, a "Find customer" action (by email), "Update
 *    customer metafield" actions for each field below, and a "Send
 *    internal email" action notifying the store owner.
 *  - Customer metafield definitions under the "custom" namespace:
 *    company_name, billing_address, company_registration,
 *    company_vat, phone, business_type (all single_line_text_field,
 *    except billing_address which is multi_line_text_field).
 */
(function () {
  'use strict';

  const API_VERSION = '2024-10';

  function generatePassword() {
    const bytes = new Uint8Array(18);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('') + 'Aa1!';
  }

  function graphqlEndpoint(shopDomain) {
    return 'https://' + shopDomain + '/api/' + API_VERSION + '/graphql.json';
  }

  async function storefrontFetch(shopDomain, token, query, variables) {
    const response = await fetch(graphqlEndpoint(shopDomain), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    });
    const json = await response.json();
    if (json.errors && json.errors.length) {
      throw new Error(json.errors.map((e) => e.message).join('; '));
    }
    return json.data;
  }

  const CUSTOMER_CREATE_MUTATION = `
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer { id email }
        customerUserErrors { field message code }
      }
    }
  `;

  const CUSTOMER_RECOVER_MUTATION = `
    mutation customerRecover($email: String!) {
      customerRecover(email: $email) {
        customerUserErrors { field message code }
      }
    }
  `;

  // Customer metafields cannot be written from the Storefront API (no
  // "metafields" field exists on CustomerUpdateInput there — that's an
  // Admin API only capability). Instead we hand the raw field values to
  // a Shopify Flow webhook, which runs server-side with Admin access
  // and writes the metafields + notifies the store owner.
  async function notifyFlowWebhook(webhookUrl, fields) {
    if (!webhookUrl) return;
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: fields.email,
          company_name: fields.companyName,
          billing_address: fields.billingAddress,
          company_registration: fields.companyRegistration || '',
          company_vat: fields.companyVat || '',
          phone: fields.phone,
          business_type: fields.businessType,
        }),
      });
    } catch (err) {
      // Non-fatal: the customer account still exists. Worst case the
      // store owner has to check the Customers list manually for this
      // one submission.
    }
  }

  async function submitTradeApplication(form, shopDomain, token, webhookUrl) {
    const data = new FormData(form);
    const fields = {
      companyName: (data.get('company_name') || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      billingAddress: (data.get('billing_address') || '').toString().trim(),
      companyRegistration: (data.get('company_registration') || '').toString().trim(),
      companyVat: (data.get('company_vat') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
      businessType: (data.get('business_type') || '').toString().trim(),
      acceptsMarketing: data.get('accepts_marketing') === 'Yes',
    };

    const password = generatePassword();

    // 1. Create the customer account
    const createResult = await storefrontFetch(shopDomain, token, CUSTOMER_CREATE_MUTATION, {
      input: {
        email: fields.email,
        password: password,
        firstName: fields.companyName,
        phone: fields.phone || undefined,
        acceptsMarketing: fields.acceptsMarketing,
      },
    });

    const createErrors = createResult.customerCreate.customerUserErrors;
    if (createErrors && createErrors.length) {
      throw new Error(createErrors.map((e) => e.message).join(' '));
    }

    // 2. Hand the application fields to the Flow webhook so it can
    //    write the customer metafields (Admin API only) and notify
    //    the store owner. Fire-and-forget: a slow/failed webhook
    //    should not block the customer from getting their account.
    notifyFlowWebhook(webhookUrl, fields);

    // 3. Send the customer a real password-reset email so they can set
    //    their own password and log in going forward.
    await storefrontFetch(shopDomain, token, CUSTOMER_RECOVER_MUTATION, { email: fields.email }).catch(function () {
      // Non-fatal — the account still exists and is reviewable even
      // if this call fails for some reason.
    });

    return fields;
  }

  function init() {
    const forms = document.querySelectorAll('[data-trade-application-form]');
    forms.forEach(function (form) {
      const shopDomain = form.dataset.shopDomain;
      const token = form.dataset.storefrontToken;
      const webhookUrl = form.dataset.flowWebhookUrl;
      const wrapper = form.closest('.trade-form') || form.parentElement;
      const successEl = wrapper.querySelector('[data-trade-form-success]');
      const errorEl = form.querySelector('[data-trade-form-error]');
      const errorTextEl = form.querySelector('[data-trade-form-error-text]');
      const submitBtn = form.querySelector('[data-trade-form-submit]');

      form.addEventListener('submit', function (event) {
        event.preventDefault();

        if (!token) {
          if (errorEl && errorTextEl) {
            errorTextEl.textContent = 'This form is not fully configured yet. Please contact us directly.';
            errorEl.hidden = false;
          }
          return;
        }

        if (errorEl) errorEl.hidden = true;
        if (submitBtn) submitBtn.disabled = true;

        submitTradeApplication(form, shopDomain, token, webhookUrl)
          .then(function () {
            form.hidden = true;
            if (successEl) {
              successEl.hidden = false;
              successEl.setAttribute('tabindex', '-1');
              successEl.focus();
            }
          })
          .catch(function (err) {
            if (errorEl && errorTextEl) {
              errorTextEl.textContent = err.message || 'Something went wrong. Please try again.';
              errorEl.hidden = false;
              errorEl.setAttribute('tabindex', '-1');
              errorEl.focus();
            }
          })
          .finally(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
